import React, { forwardRef, useMemo } from 'react';
import {
  TextField,
  TextFieldProps,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';

// Component variants using CVA for type-safe styling
const textFieldVariants = cva(
  // Base styles
  'transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        outlined: 'border border-gray-300 rounded-md',
        filled: 'bg-gray-50 border-b-2 border-gray-300',
        standard: 'border-b-2 border-gray-300',
      },
      size: {
        xs: 'h-6 text-xs px-2 py-1',
        sm: 'h-8 text-sm px-3 py-1.5',
        md: 'h-10 text-base px-4 py-2',
        lg: 'h-12 text-lg px-4 py-3',
        xl: 'h-14 text-xl px-5 py-4',
      },
      state: {
        default: '',
        error: 'border-red-500 focus:border-red-500',
        success: 'border-green-500 focus:border-green-500',
        warning: 'border-yellow-500 focus:border-yellow-500',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
    },
    defaultVariants: {
      variant: 'outlined',
      size: 'md',
      state: 'default',
      fullWidth: true,
    },
  }
);

// Enhanced TypeScript interface with all possible props
export interface SAPTextFieldProps extends Omit<TextFieldProps, 'variant' | 'size'> {
  // Custom props
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  state?: 'default' | 'error' | 'success' | 'warning';
  fullWidth?: boolean;
  
  // Enhanced accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
  
  // Loading state
  loading?: boolean;
  loadingText?: string;
  
  // Helper text with different types
  helperText?: string;
  helperTextType?: 'info' | 'error' | 'success' | 'warning';
  
  // Icon support
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  
  // Character count
  showCharacterCount?: boolean;
  maxLength?: number;
  
  // Validation
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  
  // Custom styling
  customStyles?: React.CSSProperties;
  
  // Callbacks
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  onStateChange?: (state: 'default' | 'error' | 'success' | 'warning') => void;
}

// Styled components with SAP design system
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => 
    !['state', 'loading', 'helperTextType', 'showCharacterCount', 'customStyles'].includes(prop as string),
})<SAPTextFieldProps>(({ theme, state = 'default', loading, helperTextType, showCharacterCount, customStyles }) => ({
  // Base styles
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: SAP_SPACING.component.borderRadius,
    transition: SAP_TRANSITIONS.normal,
    border: `1px solid ${getBorderColor(state)}`,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    
    // Hover state
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
      borderColor: getHoverBorderColor(state),
      // No lift/blue hover effect per spec
      transform: 'none',
      boxShadow: 'none',
    },
    
    // Focus state
    '&.Mui-focused': {
      backgroundColor: SAP_COLORS.sapWhite,
      borderColor: getFocusBorderColor(state),
      boxShadow: `0 0 0 2px ${getFocusShadowColor(state)}`,
      transform: 'none',
    },
    
    // Disabled state
    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      borderColor: SAP_COLORS.sapGray3,
      color: SAP_COLORS.sapGray4,
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
    
    // Error state
    '&.Mui-error': {
      borderColor: SAP_COLORS.sapRed,
      '&:hover': {
        borderColor: SAP_COLORS.sapRed,
      },
      '&.Mui-focused': {
        borderColor: SAP_COLORS.sapRed,
        boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightRed}`,
      },
    },
  },
  
  // Input styles
  '& .MuiOutlinedInput-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    padding: SAP_SPACING.component.padding,
    lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
    
    '&::placeholder': {
      color: SAP_COLORS.sapGray4,
      opacity: 1,
      fontStyle: 'italic',
    },
    
    // Loading state
    ...(loading && {
      color: SAP_COLORS.sapGray4,
      cursor: 'wait',
    }),
  },
  
  // Label styles
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    
    '&.Mui-focused': {
      color: getFocusLabelColor(state),
      fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    },
    
    '&.Mui-error': {
      color: SAP_COLORS.sapRed,
    },
  },
  
  // Helper text styles
  '& .MuiFormHelperText-root': {
    margin: 0,
    marginTop: SAP_SPACING.xs,
    fontSize: SAP_TYPOGRAPHY.fontSize.xs,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
    
    ...(helperTextType === 'error' && {
      color: SAP_COLORS.sapRed,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    }),
    
    ...(helperTextType === 'success' && {
      color: SAP_COLORS.sapGreen,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    }),
    
    ...(helperTextType === 'warning' && {
      color: SAP_COLORS.sapOrange,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    }),
    
    ...(helperTextType === 'info' && {
      color: SAP_COLORS.sapGray5,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    }),
  },
  
  // Character count styles
  ...(showCharacterCount && {
    '& .MuiInputBase-input': {
      paddingRight: '4rem',
    },
  }),
  
  // Custom styles override
  ...customStyles,
}));

// Utility functions for dynamic styling
function getBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapGray3;
  }
}

function getHoverBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapGray4; // light grey on hover
  }
}

function getFocusBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

function getFocusShadowColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapLightRed;
    case 'success': return SAP_COLORS.sapLightGreen;
    case 'warning': return SAP_COLORS.sapLightOrange;
    default: return SAP_COLORS.sapLightBlue;
  }
}

function getFocusLabelColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

// Character count component
const CharacterCount = ({ current, max }: { current: number; max: number }) => (
  <Typography
    variant="caption"
    sx={{
      position: 'absolute',
      right: SAP_SPACING.sm,
      top: '50%',
      transform: 'translateY(-50%)',
      color: current > max * 0.9 ? SAP_COLORS.sapOrange : SAP_COLORS.sapGray5,
      fontSize: SAP_TYPOGRAPHY.fontSize.xs,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
      pointerEvents: 'none',
    }}
  >
    {current}/{max}
  </Typography>
);

// Main component with forwardRef for proper ref handling
export const SAPTextField = forwardRef<HTMLDivElement, SAPTextFieldProps>(
  (
    {
      variant = 'outlined',
      size = 'md',
      state = 'default',
      fullWidth = true,
      loading = false,
      loadingText = 'Loading...',
      helperText,
      helperTextType = 'info',
      startIcon,
      endIcon,
      showCharacterCount = false,
      maxLength,
      validateOnBlur = true,
      validateOnChange = false,
      customStyles,
      onValidationChange,
      onStateChange,
      ariaLabel,
      ariaDescribedBy,
      value = '',
      onChange,
      onBlur,
      ...props
    },
    ref
  ) => {
    // Memoized character count
    const characterCount = useMemo(() => {
      if (!showCharacterCount || !maxLength) return null;
      const current = typeof value === 'string' ? value.length : 0;
      return { current, max: maxLength };
    }, [showCharacterCount, maxLength, value]);

    // Memoized validation state
    const validationState = useMemo(() => {
      if (!maxLength || !showCharacterCount) return state;
      const current = typeof value === 'string' ? value.length : 0;
      if (current > maxLength) return 'error';
      if (current > maxLength * 0.9) return 'warning';
      return 'success';
    }, [state, maxLength, showCharacterCount, value]);

    // Enhanced accessibility props
    const accessibilityProps = {
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-busy': loading,
      'aria-invalid': validationState === 'error',
    };

    // Enhanced input props
    const inputProps = {
      ...props.InputProps,
      startAdornment: startIcon ? (
        <InputAdornment position="start">
          {startIcon}
        </InputAdornment>
      ) : props.InputProps?.startAdornment,
      endAdornment: (
        <>
          {loading && (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          )}
          {endIcon && (
            <InputAdornment position="end">
              {endIcon}
            </InputAdornment>
          )}
          {props.InputProps?.endAdornment}
        </>
      ),
    };

    // Enhanced helper text
    const finalHelperText = useMemo(() => {
      if (loading) return loadingText;
      if (helperText) return helperText;
      if (showCharacterCount && characterCount) {
        const { current, max } = characterCount;
        if (current > max) {
          return `Maximum ${max} characters allowed`;
        }
      }
      return '';
    }, [loading, loadingText, helperText, showCharacterCount, characterCount]);

    // Enhanced helper text type
    const finalHelperTextType = useMemo(() => {
      if (loading) return 'info';
      if (helperTextType) return helperTextType;
      if (showCharacterCount && characterCount) {
        const { current, max } = characterCount;
        if (current > max) return 'error';
        if (current > max * 0.9) return 'warning';
        return 'success';
      }
      return 'info';
    }, [loading, helperTextType, showCharacterCount, characterCount]);

    return (
      <Box
        ref={ref}
        sx={{
          position: 'relative',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        <StyledTextField
          variant={variant}
          size={(size === 'xs' || size === 'sm') ? 'small' : 'medium' as any}
          state={validationState}
          fullWidth={fullWidth}
          loading={loading}
          helperTextType={finalHelperTextType}
          showCharacterCount={showCharacterCount}
          customStyles={customStyles}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          helperText={finalHelperText}
          InputProps={inputProps}
          disabled={loading || props.disabled}
          {...accessibilityProps}
          {...props}
          sx={{
            ...props.sx,
          }}
        />
        
        {/* Character count overlay */}
        {showCharacterCount && characterCount && (
          <CharacterCount
            current={characterCount.current}
            max={characterCount.max}
          />
        )}
      </Box>
    );
  }
);

// Display name for debugging
SAPTextField.displayName = 'SAPTextField';

// Export types for external use
export { textFieldVariants };
